<script setup lang="ts">
import HeaderNumber from '~/components/pages/home/NewComponents/HeaderNumber.vue'
import { getMonthDayYear, getMonthDayYearMobile } from '~/utils/day-js'
import { formatCurrencySize } from '~/utils/formatCurrency'
import { IDS_LOTTERIES_CLASSIC } from '~/utils/constants/home.js'
import { ID_KENO_FR } from '~/utils/constants/idsLotteries'
import ModalAllBalls from '~/components/pages/results/modals/ModalAllBalls.vue'

defineOptions({
	name: 'CardLotteryResult',
})

interface Props {
	contentData?: any
}

const props = withDefaults(defineProps<Props>(), {
	contentData: {},
})

const MAX_BALLS_DISPLAYED = 10
const MIN_BALLS_SHOW = 5

const { t: $t, locale } = useI18n()

const showModalAllBalls = ref<boolean>(false)

const counterBalls = computed(() => {
	const { regulars: first, extras: last = [] } = props.contentData
	return first.length + last?.length
})

const getFirstBlock = computed(() => {
	if (counterBalls.value > MAX_BALLS_DISPLAYED) return props.contentData.regulars.slice(0, MIN_BALLS_SHOW)
	return props.contentData.regulars
})

const showMultipler = computed(() => {
	const { prize, lottery_id, multiplier = null } = props.contentData
	if (prize === 0) return false
	if (lottery_id !== ID_KENO_FR) return false

	return multiplier !== null
})
</script>

<template>
	<div
		class="group relative bg-[#1b1536] sm:border border-[#595472] rounded-2xl p-6 cursor-pointer w-full transition-all duration-300 ease-out hover:-translate-y-1 flex flex-col justify-between"
	>
		<div
			class="absolute inset-0 rounded-lg bg-gradient-to-br from-[#7c76a6]/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"
		/>
		<div>
			<div class="flex items-start justify-between mb-4 gap-2">
				<div class="flex items-center gap-3">
					<div class="text-[#818798] text-lg">{{ props.contentData.flag }}</div>
					<div>
						<div class="text-white font-medium">
							{{ $t(props.contentData.name) }}
						</div>
						<div class="text-[#62748e] text-sm">
							{{ $t(props.contentData.country) }}
						</div>
					</div>
				</div>
				<div class="text-xs bg-[#314158]/50 px-2 py-1 rounded text-[#62748e] capitalize block sm:hidden min-w-fit">
					{{ getMonthDayYearMobile(props.contentData.date, locale) }}
				</div>
				<div class="text-xs bg-[#314158]/50 px-2 py-1 rounded text-[#62748e] capitalize hidden sm:block min-w-fit">
					{{ getMonthDayYear(props.contentData.date, locale) }}
				</div>
			</div>

			<div class="mb-4 space-y-4 sm:space-y-0">
				<div class="text-xs text-[#62748e] uppercase tracking-wider">Jackpot</div>
				<div class="flex gap-2 items-center">
					<p class="text-lg sm:text-2xl font-bold text-[#fbbf24]">
						{{
							props.contentData.prize === 0
								? $t('Not winner')
								: formatCurrencySize(props.contentData.prize, props.contentData.currency, 'long')
						}}
					</p>
					<div
						v-if="showMultipler"
						class="text-white text-sm font-semibold bg-primary-400 rounded-full px-2 py-px"
					>
						x{{ props.contentData.multiplier }}€
					</div>
				</div>
			</div>

			<div
				v-if="IDS_LOTTERIES_CLASSIC.includes(props.contentData.lottery_id)"
				class="text-xs text-[#62748e] uppercase tracking-wider pb-4 sm:pb-0"
			>
				{{ $t('First') }}
			</div>
			<div class="flex flex-wrap items-center gap-x-1.5 gap-y-2 sm:gap-y-4 pb-4">
				<HeaderNumber
					v-for="(number, index) in getFirstBlock"
					:key="index"
					:is-animation-active="false"
					:value="number"
					:is-empty="number === null"
					:show-btn-clear="false"
					class-icon-not-select="w-10 sm:w-12"
					class-container="bg-opacity-60 size-9 sm:size-10 text-[10px] border border-white"
				/>
				<UButton
					v-if="counterBalls > MAX_BALLS_DISPLAYED"
					square
					leading
					color="white"
					variant="soft"
					:label="`+${counterBalls - MIN_BALLS_SHOW}`"
					:ui="{
						rounded: 'rounded-full',
					}"
					class="text-neutral-50 font-semibold leading-5 hover:no-underline hover:bg-primary-400 px-2.5 py-0.5 transition-all duration-300 w-max group"
					@click.stop="showModalAllBalls = true"
				/>
				<template v-else>
					<template v-if="!IDS_LOTTERIES_CLASSIC.includes(props.contentData.lottery_id)">
						<HeaderNumber
							v-for="(number, index) in props.contentData.extras"
							:key="index"
							:is-animation-active="false"
							:value="number"
							:is-empty="number === null"
							:is-yellow-ball="true"
							:show-btn-clear="false"
							class-icon-not-select="w-10 sm:w-12"
							class-container="bg-opacity-60 size-9 sm:size-10 text-[10px] border border-yellow-300"
						/>
					</template>
				</template>
			</div>
		</div>
		<div class="border-t border-[#4b4156]">
			<p
				class="text-primary-500 text-sm pt-4 text-center sm:text-left transition-all duration-300 group-hover:text-primary-400 group-hover:translate-x-1"
			>
				{{ $t('View full analysis') }} →
			</p>
		</div>

		<ModalAllBalls v-model="showModalAllBalls">
			<div class="flex flex-wrap justify-center items-center gap-x-1.5 gap-y-2 sm:gap-y-4 sm:w-fit">
				<HeaderNumber
					v-for="(number, index) in props.contentData.regulars"
					:key="index"
					:is-animation-active="false"
					:value="number"
					:is-empty="number === null"
					:show-btn-clear="false"
					class-icon-not-select="w-10 sm:w-12"
					class-container="bg-opacity-60 size-9 sm:size-10 text-[10px] border border-white"
				/>
				<template v-if="!IDS_LOTTERIES_CLASSIC.includes(props.contentData.lottery_id)">
					<HeaderNumber
						v-for="(number, index) in props.contentData.extras"
						:key="index"
						:is-animation-active="false"
						:value="number"
						:is-empty="number === null"
						:is-yellow-ball="true"
						:show-btn-clear="false"
						class-icon-not-select="w-10 sm:w-12"
						class-container="bg-opacity-60 size-9 sm:size-10 text-[10px] border border-yellow-300"
					/>
				</template>
			</div>
		</ModalAllBalls>
	</div>
</template>
